import { useEffect, useMemo, useState } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';

import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from '@mint-lib/i18n';

import {
  Box,
  Button,
  Chip,
  Dialog,
  LoadingButton,
  SelectWithSearch,
  Typography,
} from '@myn/mui';

import { useUsers } from '../../api/queries/usersData.js';
import {
  CreateRoleType,
  RolesMetaType,
  RoleType,
  type EditRoleType,
} from '../../api/roles/index.js';
import type { UserType } from '../../api/users/users.types.js';
import FormBlock from '../../atoms/FormBlock/FormBlock.jsx';
import InputField from '../../atoms/InputField/InputField.jsx';
import TextAreaField from '../../atoms/TextAreaField/index.js';
import { getFlatErrorText } from '../../utils/getFlatErrorText.js';
import { mapUsersToSelect } from '../../utils/mapUsersToSelect.js';
import PermissionsTable from './PermissionsTable.jsx';
import type { TableDataType } from './permissionsTable.type.js';
import {
  mapInitialValues,
  mapToPermissions,
  mapToTableState,
} from './RoleModal.utils.js';
import { validationSchema, type FormValues } from './validation.js';

export const filterAgencyUsers = (
  list: UserType[],
  roleType?: string,
): UserType[] => {
  return roleType === 'agency_client_role_type'
    ? list.filter((user) => user.agencyUser)
    : list;
};

type Props = {
  className?: string;
  onClose: () => void;
  onCreate: (options: CreateRoleType) => Promise<RoleType>;
  onUpdate: (options: Partial<EditRoleType>) => Promise<RoleType>;
  initialValues?: Partial<RoleType>;
  rolesMeta: RolesMetaType;
  isActive: boolean;
  isReadonlyPermission: boolean;
};

const RoleModal: React.FC<Props> = ({
  onClose,
  onCreate,
  onUpdate,
  initialValues = {},
  rolesMeta,
  isActive,
  isReadonlyPermission,
}) => {
  const { t } = useTranslation('@myn/permissions');

  const [serverError, setServerError] = useState<string | null>(null);
  const [selectedUsers, setSelectedUsers] = useState<number[]>([]);
  const [isUsersExpanded, setIsUsersExpanded] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isPermissionsChanged, setIsPermissionsChanged] = useState(false);

  const { allUsers } = useUsers({ getFullList: true });

  const userOptions = useMemo(
    () =>
      mapUsersToSelect(
        filterAgencyUsers(allUsers.data || [], initialValues.roleType),
      ),
    [allUsers.data],
  );

  const chipsToDisplay = useMemo(() => {
    const selectedUsersToMap = isUsersExpanded
      ? selectedUsers
      : selectedUsers.slice(0, 2);

    return selectedUsersToMap.flatMap((user) => {
      return userOptions.find((option) => option.value === user) || [];
    });
  }, [isUsersExpanded, selectedUsers, userOptions]);

  const [permissions, setPermissions] = useState<TableDataType>(
    mapToTableState(
      rolesMeta.nameLabels,
      initialValues?.outputFeaturePermissions === undefined
        ? {}
        : mapInitialValues(initialValues.outputFeaturePermissions),
      {
        // @ts-ignore
        main: rolesMeta?.features
          ? Object.keys(rolesMeta?.features)?.map(
              (key) => rolesMeta?.features[key],
            )
          : [],
      },
    ),
  );

  const hasInitialValues = Object.keys(initialValues ?? {}).length > 0;
  const restValues = hasInitialValues
    ? initialValues
    : ({} as Partial<RoleType>);

  const handleSubmitForm: SubmitHandler<FormValues> = (values) => {
    setServerError(null);
    setIsSaving(true);
    const { name = '', description = '' } = values;

    const submitPromise =
      restValues?.isDefault && !restValues?.isEditable
        ? onUpdate({
            id: restValues?.id,
            users: selectedUsers,
            description,
          })
        : restValues?.isDefault && restValues?.isEditable
          ? onUpdate({
              id: restValues?.id,
              name,
              users: selectedUsers,
              description,
              inputFeaturePermissions: mapToPermissions(permissions),
            })
          : onCreate({
              ...restValues,
              name,
              users: selectedUsers,
              description,
              inputFeaturePermissions: mapToPermissions(permissions),
            });

    submitPromise
      .catch((e) => {
        const flatError = getFlatErrorText(e?.response?.data);
        setServerError(flatError);
      })
      .finally(() => setIsSaving(false));
  };

  const {
    register,
    handleSubmit,
    formState: { errors, ...restFormState },
    setValue,
    trigger,
    getValues,
    control,
    ...methods
  } = useForm<FormValues>({
    defaultValues: {
      name: initialValues.name ?? '',
      description: initialValues.description ?? '',
    },
    mode: 'onChange',
    resolver: zodResolver(validationSchema(t)),
  });

  const { name = '', description = '' } = getValues();

  const isAllRequiredFieldsFilled = !!name;
  const initialUsers = initialValues.users || [];
  const isSaveDisabled = useMemo(() => {
    return (
      (hasInitialValues
        ? initialValues.name == name &&
          initialValues.description == description &&
          initialUsers
            ?.flatMap((user) => Number(user.id))
            .toSorted((a, b) => a - b)
            .toString() === selectedUsers.toSorted((a, b) => a - b).toString()
        : !name && !description && !selectedUsers.length) &&
      !Object.keys(errors).length &&
      !isPermissionsChanged
    );
  }, [
    name,
    description,
    selectedUsers,
    errors,
    isPermissionsChanged,
    initialUsers,
  ]);

  useEffect(() => {
    methods.watch();
    if (!hasInitialValues) {
      return;
    }

    const initialSelectedUses = initialUsers
      ? initialUsers.flatMap((user) => Number(user.id) || [])
      : [];

    setSelectedUsers(initialSelectedUses);
  }, [allUsers.data]);

  const title = useMemo(() => {
    if (isReadonlyPermission) {
      return t('View role');
    }
    return hasInitialValues ? t('Edit role') : t('Add new role');
  }, [hasInitialValues, isReadonlyPermission, t]);

  return (
    <FormProvider
      {...{
        ...methods,
        register,
        handleSubmit,
        formState: { errors, ...restFormState },
        setValue,
        trigger,
        getValues,
        control,
      }}
    >
      <Dialog
        open={isActive}
        onClose={onClose}
        title={title}
        size="m"
        submitButton={
          <LoadingButton
            size="large"
            variant="contained"
            color="primary"
            onClick={handleSubmit(handleSubmitForm)}
            loading={isSaving}
            disabled={isSaveDisabled || isReadonlyPermission}
            aria-label="save: role"
          >
            {t('Save')}
          </LoadingButton>
        }
        submitButtonProps={{
          label: t('Save'),
          onClick: handleSubmit(handleSubmitForm),
          color: 'primary',
          disabled: !isAllRequiredFieldsFilled || isReadonlyPermission,
        }}
        cancelButtonProps={{
          label: t('Cancel'),
          onClick: onClose,
          variant: 'text',
        }}
      >
        <div className="flex flex-col gap-4">
          <FormBlock>
            <div className="grow">
              <InputField
                label={t('Role name*')}
                placeholder={t('Enter a name')}
                disabled={
                  restValues?.isEditable === false ||
                  isReadonlyPermission ||
                  isSaving
                }
                name="name"
              />
            </div>

            {/* TODO: migrate to tailwind - nested selector: & span */}
            <Box
              sx={{
                width: '326px',
                '& span': {
                  width: '17rem',
                },
              }}
            >
              <SelectWithSearch
                multiple
                label={t('Search and add user')}
                options={userOptions}
                value={selectedUsers}
                placeholder={t('Select from the list')}
                disabled={isReadonlyPermission || isSaving}
                onChange={(e) => {
                  setSelectedUsers(e.filter((userId) => userId));
                }}
                truncatedOptions
              />
            </Box>
          </FormBlock>
          {!!chipsToDisplay.length && (
            <div className="flex justify-between">
              <div className="flex flex-wrap gap-2 overflow-hidden pt-[7px] pr-0 pb-0 pl-0">
                {chipsToDisplay.map((user) => {
                  return <Chip key={user?.value} label={user?.label} />;
                })}
                {!isUsersExpanded && selectedUsers.length > 2 && (
                  <Chip size="medium" label={`+${selectedUsers.length - 2}`} />
                )}
              </div>
              {selectedUsers.length > 2 && (
                <div>
                  <Button
                    variant="text"
                    endIcon={isUsersExpanded ? 'ChevronUp' : 'ChevronDown'}
                    onClick={() => setIsUsersExpanded((prev) => !prev)}
                  >
                    {isUsersExpanded ? t('Hide Users') : t('See All Users')}
                  </Button>
                </div>
              )}
            </div>
          )}

          <FormBlock>
            <Typography variant="h5">{t('Assign permissions')}</Typography>
          </FormBlock>
          <FormBlock>
            <TextAreaField
              label={t('Role description')}
              placeholder={t('Enter description')}
              disabled={
                restValues?.isEditable === false ||
                isReadonlyPermission ||
                isSaving
              }
              name="description"
              fullWidth
            />
          </FormBlock>
          <FormBlock>
            <PermissionsTable
              data={permissions}
              setData={(data) => {
                setPermissions(data);
                setIsPermissionsChanged(true);
              }}
              nameLabels={rolesMeta.nameLabels}
              isDisabled={
                (restValues?.isDefault && !restValues?.isEditable) ||
                isReadonlyPermission ||
                isSaving
              }
            />
          </FormBlock>
        </div>
        {!!serverError && (
          <Typography color={'error.main'} align="center">
            {serverError}
          </Typography>
        )}
      </Dialog>
    </FormProvider>
  );
};

export default RoleModal;
